import { createStore, createEffect, createEvent, combine } from 'effector';
import { $accountByIdStore } from '../accounts';
import { $notificationStore } from '../notificationStore';
import { AlertsApi } from '@shared/api/rest';
import { ApiUtils } from '@shared/utils';
import type { IQueryParams } from '@shared/api/models';
import type {
  AddMinimalBalanceRequest,
  AlertSubscriptionResponse,
  IAlertsUpdate,
  Recipient,
  UpdateMinimalBalanceRequest,
} from '@shared/api/rest/alerts';

const addMinimalBalanceEffect = createEffect((params: IQueryParams<AddMinimalBalanceRequest>) =>
  AlertsApi.addMinimalBalance(params.request, params?.signal),
);
const updateMinimalBalanceEffect = createEffect((params: IQueryParams<IAlertsUpdate<UpdateMinimalBalanceRequest>>) =>
  AlertsApi.updateMinimalBalance(params.request, params?.signal),
);

const getRecipientEvent = createEvent<Recipient[]>();
const addRecipientEvent = createEvent<Recipient>();
const removeRecipientEvent = createEvent<string>();

updateMinimalBalanceEffect.done.watch((result) => {
  const request = result.result!.data?.accountId as number;
  $accountByIdStore.events.get(request);
});

addMinimalBalanceEffect.done.watch((result) => {
  const request = result.result!.data?.accountId as number;
  $accountByIdStore.events.get(request);
});

const clearEvent = createEvent();

const $dataStore = createStore<AlertSubscriptionResponse | null>(null)
  .on(addMinimalBalanceEffect.done, (current, data) => data.result.data! || null)
  .reset(clearEvent);

const $recipientStore = createStore<Recipient[]>([])
  .on(addRecipientEvent, (state, recipient) => [...state, recipient])
  .on(removeRecipientEvent, (state, email) => state.filter((recipient) => recipient.email !== email))
  .on(getRecipientEvent, (state, newRecipient) => newRecipient)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(addMinimalBalanceEffect.pending, (_, data) => data)
  .on(addMinimalBalanceEffect.fail, (_, data) => false)
  .on(addMinimalBalanceEffect.done, (_, data) => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(addMinimalBalanceEffect, () => null)
  .on(addMinimalBalanceEffect.fail, (_, data) => data.error.message)
  .on(addMinimalBalanceEffect.done, (_, data) => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $minimalBalanceStore = {
  events: {
    addMinimalBalance: ApiUtils.wrapperApi(addMinimalBalanceEffect),
    updateMinimalBalance: ApiUtils.wrapperApi(updateMinimalBalanceEffect),
    get: getRecipientEvent,
    add: addRecipientEvent,
    remove: removeRecipientEvent,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    recipient: $recipientStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
